import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Download, Loader2, Layers, Sparkles } from "lucide-react";
import { StickerSfxPanel } from "@/components/editor/StickerSfxPanel";
import { FramingPanel } from "@/components/editor/FramingPanel";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, PLATFORMS, ASPECTS, fmtTimePrecise } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FinalPreview } from "@/components/editor/FinalPreview";

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors duration-200 ${active ? "border-[#3ad7ff]/70 bg-[#2f7bff]/15 text-white shadow-[0_0_14px_-4px_rgba(58,215,255,0.8)]" : "border-white/10 text-slate-400 hover:text-white hover:border-white/25"}`}>{children}</button>
);
const FX = ["punch", "quake", "zoom", "shake", "flash", "bw_hit", "bloom", "neon", "chroma", "vignette", "cinema", "slowmo", "fadeout", "neon_frame", "hook"];
const fxKey = (f) => (f === "hook" ? "fx.hook_fx" : `fx.${f}`);
const field = "w-full rounded-md bg-[#070912] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#2f7bff]";

const framingOf = (cfg) => ({ crop_x: cfg.crop_x, crop_y: cfg.crop_y ?? 0.5, zoom: cfg.zoom || 1, split_ratio: cfg.split_ratio ?? 0.35, cam_rect: cfg.cam_rect || [0.68, 0.02, 0.3, 0.3], cam_zoom: cfg.cam_zoom || 1, cam_x: cfg.cam_x ?? 0.5, cam_y: cfg.cam_y ?? 0.5 });
export const makeClipBody = (video, range, cfg, active) => ({ aspect: cfg.aspect, fit: cfg.fit, quality: cfg.quality, platform: cfg.platform, video_id: video.id, start: range.start, end: range.end, ...framingOf(cfg), title: cfg.title || active?.label || "Clip CLIP-EX", description: cfg.description, effects: cfg.effects, hook: cfg.hook, stickers: cfg.stickers, sfx: cfg.sfx, music: cfg.music || null, impact: cfg.impact, pro: cfg.pro || null, subs: cfg.subs, sub_style: cfg.sub_style, sub_pos: cfg.sub_pos, sub_x: cfg.sub_pos === "custom" ? cfg.sub_x : null, sub_y: cfg.sub_pos === "custom" ? cfg.sub_y : null, sub_scale: cfg.sub_scale ?? null,
  words: cfg.words_key === `${range.start.toFixed(1)}-${range.end.toFixed(1)}` ? cfg.words : null, hashtags: cfg.hashtags.split(/\s+/).filter(Boolean), score: active?.score ?? null, hq: cfg.hq !== false, auto_face: !!cfg.auto_face && ["split", "crop"].includes(cfg.fit) });

export const ExportPanel = ({ video, range, cfg, setCfg, active, onExported, current = 0, tab = "all", onStickerAdded, onStickerRemoved, onFacecam }) => {
  const show = (k) => tab === "all" || tab === k;
  const { t, lang } = useI18n();
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [minScore, setMinScore] = useState(0);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const genAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/captions", { video_id: video.id, platform: cfg.platform, start: range.start, end: range.end, label: active?.label || cfg.title, reason: active?.reason || "", lang });
      setCfg((c) => ({ ...c, title: data.title, description: [data.hook, data.description].filter(Boolean).join("\n\n"), hashtags: data.hashtags.join(" ") }));
      toast.success(t("ex.ai_done"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setAiBusy(false); }
  };

  const framingBody = () => framingOf(cfg);
  const payload = () => ({ aspect: cfg.aspect, fit: cfg.fit, quality: cfg.quality, platform: cfg.platform });
  const toggleFx = (f) => setCfg((c) => ({ ...c, effects: c.effects.includes(f) ? c.effects.filter((x) => x !== f) : [...c.effects, f] }));
  const nav = useNavigate();
  const creditError = (e) => (e?.response?.status === 402
    ? toast.error(formatApiError(e), { action: { label: t("cr.get"), onClick: () => nav("/credits") }, duration: 8000 })
    : toast.error(formatApiError(e)));
  const clipBody = () => makeClipBody(video, range, cfg, active);
  const applyAiFx = () => {
    setCfg((c) => ({ ...c, effects: Array.from(new Set([...(active?.effects || []), "watermark"])), hook: active?.hook || c.hook }));
    toast.success(t("fx.applied"));
  };

  const exportClip = async () => {
    setBusy(true);
    try {
      await api.post("/clips", clipBody());
      toast.success(t("ex.started"), { description: t("ex.started_sub") });
      onExported();
    } catch (e) { creditError(e); } finally { setBusy(false); }
  };

  const autoAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/videos/${video.id}/auto-clips`, { ...payload(), ...framingBody(), min_score: minScore, use_ai_effects: true, extra_effects: cfg.effects, auto_face: cfg.auto_face !== false });
      toast.success(t("ex.auto_started", { n: data.length }));
      onExported();
    } catch (e) { creditError(e); } finally { setBusy(false); }
  };

  return (
    <div className="cx-card rounded-xl p-5 space-y-5" data-testid="export-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="cx-label">{t("ex.title")}</div>
        <span className="font-mono text-xs text-slate-400" data-testid="export-range-display">{fmtTimePrecise(range.start)} → {fmtTimePrecise(range.end)} · {(range.end - range.start).toFixed(1)}s</span>
      </div>
      {show("format") && <>
      <div>
        <div className="text-[11px] text-slate-500 mb-2">{t("ex.platform")}</div>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => <Chip key={p.id} testid={`platform-${p.id}`} active={cfg.platform === p.id} onClick={() => setCfg((c) => ({ ...c, platform: p.id, aspect: p.aspect }))}>{p.label}</Chip>)}
        </div>
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-2">{t("ex.format")}</div>
        <div className="grid grid-cols-4 gap-2">
          {ASPECTS.map((a) => <Chip key={a.id} testid={`aspect-ratio-${a.id.replace(":", "-")}`} active={cfg.aspect === a.id} onClick={() => set("aspect", a.id)}>{a.label}</Chip>)}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] text-slate-500 mb-2">{t("ex.framing")}</div>
          <div className="flex flex-wrap gap-2">
            <Chip testid="fit-crop" active={cfg.fit === "crop"} onClick={() => set("fit", "crop")}>{t("ex.crop")}</Chip>
            <Chip testid="fit-blur" active={cfg.fit === "blur"} onClick={() => set("fit", "blur")}>{t("ex.blur")}</Chip>
            <Chip testid="fit-bars" active={cfg.fit === "bars"} onClick={() => set("fit", "bars")}>{t("ex.bars")}</Chip>
            {cfg.aspect === "9:16" && <Chip testid="fit-split" active={cfg.fit === "split"} onClick={() => set("fit", "split")}>{t("ex.split")}</Chip>}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500 mb-2">{t("ex.quality")}</div>
          <div className="flex flex-wrap gap-2">
            <Chip testid="quality-1080" active={cfg.quality === "1080"} onClick={() => set("quality", "1080")}>1080p</Chip>
            <Chip testid="quality-720" active={cfg.quality === "720"} onClick={() => set("quality", "720")}>720p</Chip>
            <Chip testid="quality-pro-toggle" active={cfg.hq !== false} onClick={() => set("hq", cfg.hq === false)}>✦ {t("hq.label")}</Chip>
          </div>
          {cfg.hq !== false && <p className="text-[10px] text-slate-500 mt-1.5" data-testid="quality-pro-hint">{t("hq.hint")}</p>}
        </div>
      </div>
      <FramingPanel video={video} cfg={cfg} setCfg={setCfg} current={current} onFacecam={onFacecam} />
      </>}
      {show("fx") && <div className="border-t border-white/5 pt-4" data-testid="effects-panel">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-[11px] text-slate-500">{t("fx.title")}</div>
          {active?.effects?.length > 0 && (
            <button data-testid="apply-ai-effects-btn" onClick={applyAiFx} className="cx-ghost rounded-full px-3 py-1.5 text-xs text-[#c79bff] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {t("fx.ai")}</button>
          )}
        </div>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5 mb-3 cursor-pointer" data-testid="watermark-toggle-row">
          <span className="flex items-center gap-2 min-w-0">
            <img src="/logo-x.png" alt="" className="w-7 h-auto shrink-0" />
            <span className="min-w-0">
              <span className="block text-xs text-white">{t("wm.label")}</span>
              <span className="block text-[10px] text-slate-500">{t("wm.soon")}</span>
            </span>
          </span>
          <Switch data-testid="watermark-switch" checked={cfg.effects.includes("watermark")} onCheckedChange={() => toggleFx("watermark")} />
        </label>
        <div className="flex flex-wrap gap-2">
          {FX.map((f) => <Chip key={f} testid={`fx-${f}`} active={cfg.effects.includes(f)} onClick={() => toggleFx(f)}>{t(fxKey(f))}</Chip>)}
        </div>
        {cfg.effects.includes("hook") && (
          <input data-testid="clip-hook-input" value={cfg.hook} onChange={(e) => set("hook", e.target.value)} maxLength={60} placeholder={t("fx.hook")} className={`${field} mt-3 font-display uppercase`} />
        )}
      </div>}
      {show("stickers") && <StickerSfxPanel mode="stickers" cfg={cfg} setCfg={setCfg} onAdded={onStickerAdded} onRemoved={onStickerRemoved} rel={Math.min(Math.max(current - range.start, 0), range.end - range.start)} />}
      {show("audio") && <StickerSfxPanel mode="audio" cfg={cfg} setCfg={setCfg} len={range.end - range.start} rel={Math.min(Math.max(current - range.start, 0), range.end - range.start)} />}
      {show("export") && <>
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-500">{t("ex.post_text")}</div>
          <button data-testid="generate-ai-captions-btn" onClick={genAI} disabled={aiBusy} className="cx-ghost rounded-full px-3 py-1.5 text-xs text-[#c79bff] flex items-center gap-1.5">
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {t("ex.gen_ai")}
          </button>
        </div>
        <input data-testid="clip-title-input" value={cfg.title} onChange={(e) => set("title", e.target.value)} placeholder={t("ex.clip_title")} className={field} />
        <textarea data-testid="clip-description-input" rows={3} value={cfg.description} onChange={(e) => set("description", e.target.value)} placeholder={t("ex.caption")} className={`${field} resize-none`} />
        <input data-testid="clip-hashtags-input" value={cfg.hashtags} onChange={(e) => set("hashtags", e.target.value)} placeholder="#gaming #clip" className={`${field} font-mono text-[#3ad7ff]`} />
      </div>
      </>}
      <div className="sticky bottom-0 -mx-5 -mb-5 px-5 pb-5 pt-3 bg-[#0b0e1a]/95 backdrop-blur border-t border-white/5 rounded-b-xl space-y-2">
      <FinalPreview getBody={clipBody} />
      <button data-testid="export-clip-btn" onClick={exportClip} disabled={busy || aiBusy} className="cx-btn w-full rounded-full py-3.5 font-display font-semibold tracking-wide flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("ex.export")}
      </button>
      </div>
      {show("export") && video.highlights.length > 0 && (
        <div className="rounded-lg border border-[#a746ff]/25 bg-[#a746ff]/[0.05] p-4" data-testid="auto-clips-panel">
          <div className="flex items-center justify-between text-xs text-slate-300">
            <span>{t("ex.min_score")}</span><span className="font-mono text-[#c79bff]">{minScore}</span>
          </div>
          <Slider className="mt-3" value={[minScore]} max={100} step={5} onValueChange={([v]) => setMinScore(v)} />
          <button data-testid="auto-clips-btn" onClick={autoAll} disabled={busy} className="cx-ghost w-full mt-4 rounded-full py-2.5 text-sm text-white flex items-center justify-center gap-2">
            <Layers className="w-4 h-4 text-[#c79bff]" /> {t("ex.auto_all", { n: video.highlights.filter((h) => h.score >= minScore).length })}
          </button>
          <p className="text-[10px] text-slate-500 mt-2 text-center" data-testid="auto-clips-raw-hint">{t("ex.auto_raw_hint")}</p>
        </div>
      )}
    </div>
  );
};
